import React from 'react';

const STATUS_BADGES = {
  completed: {
    bg: 'bg-emerald-900/20',
    text: 'text-emerald-400',
    label: 'Completed'
  },
  failed: {
    bg: 'bg-rose-900/20',
    text: 'text-rose-400',
    label: 'Failed'
  },
  running: {
    bg: 'bg-sky-900/20',
    text: 'text-sky-400',
    label: 'Running'
  },
  pending: {
    bg: 'bg-amber-900/20',
    text: 'text-amber-400',
    label: 'Pending'
  },
  unknown: {
    bg: 'bg-slate-800/60',
    text: 'text-slate-400',
    label: 'Unknown'
  }
};

const getStatusBadge = (status) => {
  const badge = STATUS_BADGES[status] || STATUS_BADGES.pending;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${badge.bg} ${badge.text}`}>
      {status === 'running' && (
        <span className="inline-block h-2 w-2 rounded-full bg-sky-400 animate-pulse mr-1.5"></span>
      )}
      {badge.label}
    </span>
  );
};

const getSeverityDots = (issueBySeverity) => {
  if (!issueBySeverity) {
    return <span className="text-[11px]" style={{color:'rgba(255,255,255,0.4)'}}>No issues</span>;
  }

  return (
    <div className="flex items-center gap-2 text-[11px]" style={{color:'rgba(255,255,255,0.55)'}}>
      <span className="inline-flex items-center gap-1">
        <span className="h-2 w-2 rounded-full bg-rose-500"></span>
        {issueBySeverity.Error || 0}
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="h-2 w-2 rounded-full bg-amber-400"></span>
        {issueBySeverity.Warning || 0}
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="h-2 w-2 rounded-full bg-sky-500"></span>
        {issueBySeverity.Info || 0}
      </span>
    </div>
  );
};

const AnalyzerProcessList = ({ processes = [], stats = {}, selectedProcessId, onSelectProcess }) => {
  return (
    <div className="w-full lg:w-72 flex flex-col" style={{background:'var(--tqa-surface-2)',borderRight:'1px solid var(--tqa-border-dark)'}}>
      <div className="p-4" style={{borderBottom:'1px solid var(--tqa-border-dark)'}}>
        <div className="text-xs font-semibold uppercase tracking-wide mb-2" style={{color:'rgba(255,255,255,0.45)'}}>
          Analyzer Summary
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-[11px]">
          <div className="rounded-md p-2" style={{background:'var(--tqa-surface-3)',border:'1px solid var(--tqa-border-dark)'}}>
            <div style={{color:'rgba(255,255,255,0.4)'}}>Total</div>
            <div className="font-semibold text-sm" style={{color:'rgba(255,255,255,0.85)'}}>{stats.total || 0}</div>
          </div>
          <div className="rounded-md p-2" style={{background:'var(--tqa-surface-3)',border:'1px solid var(--tqa-border-dark)'}}>
            <div style={{color:'rgba(255,255,255,0.4)'}}>Completed</div>
            <div className="text-emerald-400 font-semibold text-sm">{stats.completed || 0}</div>
          </div>
          <div className="rounded-md p-2" style={{background:'var(--tqa-surface-3)',border:'1px solid var(--tqa-border-dark)'}}>
            <div style={{color:'rgba(255,255,255,0.4)'}}>Failed</div>
            <div className="text-rose-400 font-semibold text-sm">{stats.failed || 0}</div>
          </div>
          <div className="rounded-md p-2" style={{background:'var(--tqa-surface-3)',border:'1px solid var(--tqa-border-dark)'}}>
            <div style={{color:'rgba(255,255,255,0.4)'}}>Running</div>
            <div className="text-sky-400 font-semibold text-sm">{stats.running || 0}</div>
          </div>
          <div className="rounded-md p-2" style={{background:'var(--tqa-surface-3)',border:'1px solid var(--tqa-border-dark)'}}>
            <div style={{color:'rgba(255,255,255,0.4)'}}>Pending</div>
            <div className="text-amber-400 font-semibold text-sm">{stats.pending || 0}</div>
          </div>
          <div className="rounded-md p-2" style={{background:'var(--tqa-surface-3)',border:'1px solid var(--tqa-border-dark)'}}>
            <div style={{color:'rgba(255,255,255,0.4)'}}>Unknown</div>
            <div className="text-slate-400 font-semibold text-sm">{stats.unknown || 0}</div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {processes.length === 0 ? (
          <div className="text-sm text-center py-8" style={{color:'rgba(255,255,255,0.4)'}}>
            Upload a UiPath project to see analyzer results.
          </div>
        ) : (
          processes.map((process) => {
            const isSelected = selectedProcessId === process.id;
            return (
              <button
                key={process.id}
                onClick={() => onSelectProcess(process)}
                className={`w-full text-left border rounded-lg p-3 transition-all ${
                  isSelected
                    ? 'border-tqa-accent shadow-sm ring-2 ring-tqa-accent/20'
                    : 'hover:border-white/20'
                }`}
                style={isSelected ? {background:'var(--tqa-surface-3)'} : {background:'var(--tqa-surface-3)',borderColor:'var(--tqa-border-dark)'}}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold text-sm truncate" style={{color:'rgba(255,255,255,0.85)'}}>
                    {process.name}
                  </div>
                  {getStatusBadge(process.analyzerStatus)}
                </div>
                <div className="text-[11px] mb-2" style={{color:'rgba(255,255,255,0.5)'}}>
                  {process.technicalName || 'Workflow'}
                  {process.version ? ` • v${process.version}` : ''}
                </div>
                {process.analyzerStatus === 'completed' && process.hasResults ? (
                  <div className="flex items-center justify-between">
                    <div className="text-[11px]" style={{color:'rgba(255,255,255,0.5)'}}>{process.issueCount} issues</div>
                    {getSeverityDots(process.issueBySeverity)}
                  </div>
                ) : (
                  <div className="text-[11px]" style={{color:'rgba(255,255,255,0.35)'}}>
                    {process.analyzerStatus === 'failed'
                      ? 'Analysis failed'
                      : process.analyzerStatus === 'running'
                        ? 'Analysis in progress'
                        : process.analyzerStatus === 'unknown'
                          ? 'Status unknown'
                          : 'No results yet'}
                  </div>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AnalyzerProcessList;
